import React from 'react';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ScrollArea } from '@cotera/client/v0/components/ui/scroll-area';
import { cn } from '@cotera/client/v0/lib/utils';

export function ResourceListEmptyState({
  emptyIcon,
  emptyText,
  noMatchText,
  searchQueryTrimmed,
  primaryAction,
  onClearFilters,
  filtersActive = false,
  compact = false,
  smallIcon = false,
}: {
  emptyIcon: React.ReactNode;
  emptyText: string;
  noMatchText: string;
  searchQueryTrimmed: string;
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  /**
   * Resets every active filter (type / ownership / search). Offered whenever
   * something is narrowing the list, so an empty result is always one click
   * away from "show me everything" instead of a dead end.
   */
  onClearFilters?: () => void;
  /** Whether any filter (beyond search) is currently narrowing the list. */
  filtersActive?: boolean;
  /** Smaller padding for drawer / bottom-panel hosts. */
  compact?: boolean;
  /**
   * Scales the icon down for hosts narrower than a page.
   *
   * The list pages all pass a `h-12 w-12` icon, which is right at page width
   * and oversized in a half-width artifact pane. A middle tier between that and
   * `compact` — which drops the icon altogether — keeps the visual anchor
   * without it dominating the pane.
   */
  smallIcon?: boolean;
}): React.ReactElement {
  const isNarrowed = searchQueryTrimmed !== '' || filtersActive;
  const showClear = isNarrowed && onClearFilters !== undefined;
  return (
    <ScrollArea className="flex-1 min-h-0 -mr-4 pr-4">
      <div
        className={
          compact
            ? 'flex flex-col items-center justify-center py-8 text-center'
            : 'flex flex-col items-center justify-center py-20 text-center'
        }
      >
        {compact ? null : (
          // Beats the icon's own `h-12 w-12`: the child selector is the more
          // specific rule, so callers keep passing one sized for a page.
          <span className={cn(smallIcon && '[&>svg]:size-8 [&>svg]:mb-3')}>
            {emptyIcon}
          </span>
        )}
        <Text.P className="text-muted-foreground mt-1">
          {isNarrowed ? noMatchText : emptyText}
        </Text.P>
        <div className="flex items-center gap-2">
          {showClear && (
            <Button onClick={onClearFilters} variant="outline" className="mt-5">
              Clear filters
            </Button>
          )}
          {/* A narrowed-to-empty list is a filtering problem, not an empty
              workspace — offering "create" there answers the wrong question. */}
          {!isNarrowed && primaryAction !== undefined && (
            <Button
              onClick={primaryAction.onClick}
              variant="outline"
              icon="plus"
              className="mt-5"
            >
              {primaryAction.label}
            </Button>
          )}
        </div>
      </div>
      {compact ? null : <div className="pb-8" />}
    </ScrollArea>
  );
}
